import React from "react";
import "./StageStepper.scss";

/**
 * CAPA-style circular stage stepper (nodes + connector + labels).
 *
 * @param {{
 *   steps: Array<{ label: string, icon?: string, id?: string }>;
 *   selectedIndex?: number;
 *   completedIndices?: number[];
 *   onStepClick?: (index: number) => void;
 *   ariaLabel?: string;
 *   className?: string;
 * }} props
 */
function StageStepper({
  steps = [],
  selectedIndex = 0,
  completedIndices = [],
  onStepClick,
  ariaLabel = "Steps",
  className = "",
}) {
  const completedSet = new Set(completedIndices);
  const activeIndex = Math.min(
    Math.max(0, selectedIndex),
    Math.max(0, steps.length - 1),
  );

  return (
    <div className={`stage-stepper-outer ${className}`.trim()}>
      <nav
        className="stage-stepper"
        style={{
          gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, minmax(0, 1fr))`,
        }}
        aria-label={ariaLabel}
      >
        {steps.map((step, index) => {
          const complete = completedSet.has(index);
          const selected = index === activeIndex;
          const active = selected && !complete;
          // Green connector only when this step is truly complete — not merely because we navigated ahead.
          const connectorDone = index < steps.length - 1 && complete;
          const iconClass = step.icon ? `fa-${step.icon}` : "fa-circle";

          let nodeClass =
            "stage-stepper__node stage-stepper__node--upcoming";
          if (complete) {
            nodeClass =
              "stage-stepper__node stage-stepper__node--complete";
          } else if (active) {
            nodeClass = "stage-stepper__node stage-stepper__node--active";
          }
          if (selected) {
            nodeClass += " stage-stepper__node--selected";
          }

          let labelClass = "stage-stepper__label--upcoming";
          if (selected) {
            labelClass = complete
              ? "stage-stepper__label--selected stage-stepper__label--selected-complete"
              : "stage-stepper__label--selected";
          } else if (complete) {
            labelClass = "stage-stepper__label--complete";
          }

          return (
            <button
              key={step.id || step.label || index}
              type="button"
              className={`stage-stepper__segment${
                connectorDone ? " stage-stepper__segment--connector-done" : ""
              }`}
              title={step.label}
              onClick={() => onStepClick?.(index)}
            >
              <div className="stage-stepper__node-wrap">
                <div className={nodeClass}>
                  {complete ? (
                    <i className="fas fa-check" aria-hidden />
                  ) : (
                    <i className={`fas ${iconClass}`} aria-hidden />
                  )}
                </div>
              </div>
              <div className={`stage-stepper__label ${labelClass}`}>
                {step.label}
              </div>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default StageStepper;
